/**
 * useChatProviderOptions — shared source of truth for the chat model
 * picker that lives in both the Andrei floating panel and the
 * JobWorkspace chat header.
 *
 * What it returns: a single merged `options` array of
 * { value, label } pairs:
 *   - Fixed direct-provider entries (Claude Sonnet/Opus direct,
 *     Gemini Flash/Pro direct)
 *   - Dynamic OpenRouter entries fetched from the
 *     `getOpenRouterModels` Cloud Function (with a seeded fallback
 *     until the network call resolves)
 *
 * Value grammar matches what the cloud handlers expect on the wire:
 *   'anthropic' | 'anthropic:opus'
 *   'gemini' | 'gemini:flash' | 'gemini:pro'
 *   'openrouter:<provider>/<model>'
 *
 * The hook fetches the OR list at most once per mount. Components
 * mount/unmount cheaply (single Settings doc read; cached by the
 * cloud function) so we don't bother memoising across hook instances.
 */

import { useEffect, useState } from 'react';
import { authFetch } from '../../utils/cloudFunctionsAuth';

export interface ProviderOption {
  value: string;
  label: string;
}

const OR_LIST_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/getOpenRouterModels';

/** Fixed (non-OpenRouter) entries. Andrei now routes exclusively through
 *  OpenRouter (the direct Anthropic/Gemini options were removed 2026-06
 *  when the Anthropic key was disabled). The only fixed entry is the
 *  "Settings default" sentinel: an EMPTY value, which makes the panel send
 *  no provider hint so the server uses the andreiChat model configured on
 *  the AI/LLM settings page. Everything else comes from the OpenRouter list. */
export const FIXED_PROVIDER_OPTIONS: ProviderOption[] = [
  { value: '', label: '⚙ Settings default (OpenRouter)' },
];

/** Fallback seed for OpenRouter entries — used until /getOpenRouterModels
 *  responds so the dropdown isn't briefly empty. Matches the server-side
 *  DEFAULT_OPENROUTER_MODELS list. */
const OR_SEED: ProviderOption[] = [
  { value: 'openrouter:anthropic/claude-sonnet-4.6', label: 'Claude Sonnet 4.6 (via OpenRouter)' },
  { value: 'openrouter:anthropic/claude-opus-4',     label: 'Claude Opus 4 (via OpenRouter)' },
  { value: 'openrouter:openai/gpt-5',                label: 'GPT-5 (via OpenRouter)' },
  { value: 'openrouter:openai/gpt-5-mini',           label: 'GPT-5 mini (via OpenRouter)' },
  { value: 'openrouter:google/gemini-2.5-pro',       label: 'Gemini 2.5 Pro (via OpenRouter)' },
  { value: 'openrouter:z-ai/glm-5.1',                label: 'GLM 5.1 (via OpenRouter)' },
  { value: 'openrouter:x-ai/grok-4',                 label: 'Grok 4 (via OpenRouter)' },
  { value: 'openrouter:deepseek/deepseek-r1',        label: 'DeepSeek R1 (via OpenRouter)' },
];

export interface UseChatProviderOptionsResult {
  options: ProviderOption[];
  /** True while the OR-models endpoint is in flight on first load.
   *  Components that want to disable the select can use this; most
   *  don't bother because the seed list keeps the UI usable. */
  loading: boolean;
}

export function useChatProviderOptions(): UseChatProviderOptionsResult {
  const [openRouterOptions, setOpenRouterOptions] = useState<ProviderOption[]>(OR_SEED);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await authFetch(OR_LIST_URL, { method: 'GET' });
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled) return;
        if (Array.isArray(data?.models)) {
          const next: ProviderOption[] = [];
          for (const m of data.models) {
            if (m && typeof m.id === 'string' && m.id.trim()) {
              next.push({
                value: m.id,
                label: typeof m.label === 'string' && m.label.trim() ? m.label : m.id,
              });
            }
          }
          // Empty list means the user explicitly cleared it — honour that.
          setOpenRouterOptions(next);
        }
      } catch { /* silent — keep the seed */ }
      finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return {
    options: [...FIXED_PROVIDER_OPTIONS, ...openRouterOptions],
    loading,
  };
}

/** Helper for components that need to render a stored value not
 *  present in the merged options (e.g. localStorage held an old or
 *  removed OpenRouter id). Returns the options array with the
 *  unknown value pre-pended as "(custom)" so the user can see what's
 *  there and switch away from it. */
export function withCustomValue(options: ProviderOption[], currentValue: string | undefined): ProviderOption[] {
  if (!currentValue) return options;
  if (options.some((o) => o.value === currentValue)) return options;
  return [{ value: currentValue, label: `${currentValue} (custom)` }, ...options];
}
